/**
 * Collections: the Weekly Collection Lineup (Zuper schedule + HubSpot amounts), one week at a time.
 * Data is written by the "lineup" Cloud Functions into lineups/{monday} and lineup_pdfs/{monday};
 * this page only reads. Visible only to users in config/roles.collections (Admin toggle; nav hidden otherwise, Firestore rules enforce).
 *
 * "Collected" on the chart and details strip = every succeeded/processing HubSpot payment dated in
 * the week (any deal). The by-PM collected bars are payments on that week's lineup jobs only.
 */
import { useEffect, useMemo, useState } from 'react';
import { getLineupPdf, useLineups, type Lineup, type LineupRow, type Me } from '../lib/data';
import { Empty } from '../ui';

const TARGET = '#6eabdd';
const CASH = '#f0a83c';
const usd = (n: number) => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const k = (n: number) => (n >= 1e6 ? '$' + (n / 1e6).toFixed(1) + 'M' : n >= 1000 ? '$' + Math.round(n / 1000) + 'K' : '$' + Math.round(n));
const parts = (ymd: string) => new Date(ymd + 'T12:00:00Z');
const fmtDay = (ymd: string, o: Intl.DateTimeFormatOptions) => parts(ymd).toLocaleDateString('en-US', { ...o, timeZone: 'UTC' });
const short = (ymd: string) => fmtDay(ymd, { month: 'short', day: 'numeric' });
const range = (w: Lineup) => `${fmtDay(w.monday, { weekday: 'short', month: 'short', day: 'numeric' })} - ${fmtDay(w.sunday, { weekday: 'short', month: 'short', day: 'numeric' })}`;
const md = (ymd: string) => `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}`;

function todayMonday(now = new Date()): string {
  const ymd = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(now);
  const d = parts(ymd);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
}

type Status = 'Complete' | 'In progress' | 'Next week';
const weekStatus = (monday: string, cur: string): Status => (monday > cur ? 'Next week' : monday === cur ? 'In progress' : 'Complete');
const STATUS_COLOR: Record<Status, string> = { Complete: 'var(--st-good)', 'In progress': 'var(--st-active)', 'Next week': 'var(--sky-hi)' };

/** Older docs may lack the newer totals; fall back to the rows. */
function figures(w: Lineup) {
  const t = w.totals;
  const dep = w.rows.filter((r) => r.basis === '50% deposit');
  const cReroof = t.collectedReroof ?? w.rows.filter((r) => r.type === 'reroof').reduce((s, r) => s + (r.collectedInWeek || 0), 0);
  const cRepair = t.collectedRepair ?? w.rows.filter((r) => r.type === 'repair').reduce((s, r) => s + (r.collectedInWeek || 0), 0);
  return {
    target: t.all, reroof: t.reroof, repair: t.repair, reroofJobs: t.reroofJobs, repairJobs: t.repairJobs,
    deposits: t.deposits ?? dep.reduce((s, r) => s + r.target, 0),
    depositJobs: t.depositJobs ?? dep.length,
    cash: typeof t.collectedAllCash === 'number' ? t.collectedAllCash : null, // null = daily refresh has not filled this week yet
    cReroof, cRepair,
  };
}

function pmList(w: Lineup) {
  if (w.byPm?.length) return w.byPm;
  const m: Record<string, { pm: string; target: number; collected: number; jobs: number }> = {};
  for (const r of w.rows) { const n = r.pm || 'No PM assigned'; const o = m[n] ?? (m[n] = { pm: n, target: 0, collected: 0, jobs: 0 }); o.target += r.target; o.collected += r.collectedInWeek || 0; o.jobs++; }
  return Object.values(m).sort((a, b) => b.target - a.target);
}

export function Collections({ me, access }: { me: Me; access: boolean | null }) {
  if (access === null) return <p className="muted">Loading...</p>;
  if (!access) return <NoAccess />;
  return <CollectionsLoaded me={me} />;
}

function CollectionsLoaded({ me }: { me: Me }) {
  const { weeks, error } = useLineups(me);
  return <CollectionsView weeks={weeks} error={error} />;
}

export function NoAccess() {
  return <div className="card"><h2>Collections</h2><p className="muted">You don't have access to Collections.</p></div>;
}

export function CollectionsView({ weeks, error, now }: { weeks: Lineup[]; error?: string | null; now?: Date }) {
  const cur = todayMonday(now);
  const [pick, setPick] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'reroof' | 'repair'>('all');
  const sel = Math.max(0, weeks.findIndex((w) => w.monday === (pick ?? weeks[weeks.length - 1]?.monday)));
  const w = weeks[sel];

  if (error) return <p className="err">{error}</p>;
  if (!w) return <div className="card"><Empty>No lineups yet. The first one is written by the daily lineup refresh (6 AM) or the Friday 3 PM run.</Empty></div>;

  const go = (i: number) => setPick(weeks[Math.max(0, Math.min(weeks.length - 1, i))].monday);
  const f = figures(w);
  const prev = sel > 0 ? weeks[sel - 1] : null;
  const delta = prev && prev.totals.all ? ((w.totals.all - prev.totals.all) / prev.totals.all) * 100 : null;
  const status = weekStatus(w.monday, cur);
  const rows = w.rows.filter((r) => filter === 'all' || r.type === filter);

  return (
    <div className="coll">
      <div className="toolbar coll-head">
        <div>
          <h2 className="page-title">Weekly Collection Lineup</h2>
          <div className="muted">{range(w)} - <span style={{ color: STATUS_COLOR[status] }}>{status}</span> - Schedule from Zuper, amounts from HubSpot</div>
        </div>
        <span className="spacer" />
        <div className="coll-nav">
          <button type="button" className="secondary" aria-label="Previous week" disabled={sel === 0} onClick={() => go(sel - 1)}>&lsaquo;</button>
          <div role="group" aria-label="Choose week" className="coll-pills">
            {weeks.map((x) => (
              <button key={x.monday} type="button" className={x.monday === w.monday ? 'on' : 'secondary'} aria-pressed={x.monday === w.monday} onClick={() => setPick(x.monday)}>{short(x.monday)}</button>
            ))}
          </div>
          <button type="button" className="secondary" aria-label="Next week" disabled={sel === weeks.length - 1} onClick={() => go(sel + 1)}>&rsaquo;</button>
          <PdfButton monday={w.monday} />
        </div>
      </div>

      <section aria-label="Week totals" className="stat-tiles">
        <div className="stat"><span className="stat-label">Total lineup</span><span className="stat-num">{usd(f.target)}</span>
          <span className="stat-label">{f.reroofJobs + f.repairJobs} jobs - {delta === null ? 'first week tracked' : `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}% vs prior week`}</span></div>
        <div className="stat"><span className="stat-label">Re-roof</span><span className="stat-num">{usd(f.reroof)}</span><span className="stat-label">{f.reroofJobs} jobs</span></div>
        <div className="stat"><span className="stat-label">Repair / service</span><span className="stat-num">{usd(f.repair)}</span><span className="stat-label">{f.repairJobs} repairs</span></div>
        <div className="stat amber"><span className="stat-label">Deposits to collect</span><span className="stat-num">{usd(f.deposits)}</span>
          <span className="stat-label">{f.depositJobs} {f.depositJobs === 1 ? 'job' : 'jobs'} - included in re-roof</span></div>
      </section>

      <section className="card">
        <div className="coll-chart-head">
          <h2>Lineup target vs. collected, by week</h2>
          <div className="coll-legend">
            <span><i style={{ background: TARGET }} />Lineup target</span>
            <span><i style={{ background: CASH }} />Collected that week</span>
            <span>Click a week for details</span>
          </div>
        </div>
        <WeekChart weeks={weeks} sel={sel} cur={cur} onPick={(i) => go(i)} />
        <div className="coll-details">
          <div><span className="stat-label">Week of {short(w.monday)}</span><div style={{ color: STATUS_COLOR[status] }}>{status}</div></div>
          <div><span className="stat-label">Lineup target</span><div className="coll-big" style={{ color: 'var(--sky-hi)' }}>{usd(f.target)}</div></div>
          <div><span className="stat-label">Collected</span>
            <div className="coll-big" style={{ color: '#f5c27a' }}>{status === 'Next week' ? 'Not started' : f.cash === null ? 'Not refreshed yet' : usd(f.cash)}</div>
            <span className="stat-label">{status === 'Next week' ? 'Week has not started' : f.cash === null ? 'Waiting for the daily refresh' : `${f.target ? Math.round(((f.cash ?? 0) / f.target) * 100) : 0}% of target${status === 'In progress' ? ' so far' : ''}`}</span></div>
          <div><span className="stat-label">Re-roof target</span><div className="coll-mid">{usd(f.reroof)}</div></div>
          <div><span className="stat-label">Repair target</span><div className="coll-mid">{usd(f.repair)}</div></div>
        </div>
        <p className="muted small">Collected = every payment received Mon-Sun in HubSpot, on any deal. Past weeks are rebuilt from where jobs ended up in Zuper, so rescheduled jobs show in the week they actually ran.</p>
      </section>

      <div className="coll-cols">
        <PmBars w={w} />
        <Watch w={w} status={status} />
      </div>

      <section className="card">
        <div className="toolbar">
          <h2 style={{ margin: 0 }}>Jobs - week of {short(w.monday)}</h2>
          <span className="spacer" />
          <div role="group" aria-label="Filter jobs" className="coll-filter">
            {([['all', `All ${w.rows.length}`], ['reroof', `Re-roof ${f.reroofJobs}`], ['repair', `Repair ${f.repairJobs}`]] as const).map(([id, label]) => (
              <button key={id} type="button" className={filter === id ? 'on' : 'secondary'} aria-pressed={filter === id} onClick={() => setFilter(id)}>{label}</button>
            ))}
          </div>
        </div>
        <JobsTable rows={rows} showCollected={status !== 'Next week'} />
      </section>
    </div>
  );
}

function PdfButton({ monday }: { monday: string }) {
  const [state, setState] = useState<'checking' | 'ready' | 'none'>('checking');
  const [pdf, setPdf] = useState<{ pdf: string; name: string } | null>(null);
  useEffect(() => {
    let live = true;
    setState('checking'); setPdf(null);
    getLineupPdf(monday).then((p) => { if (live) { setPdf(p?.pdf ? p : null); setState(p?.pdf ? 'ready' : 'none'); } }).catch(() => { if (live) setState('none'); });
    return () => { live = false; };
  }, [monday]);
  const download = () => {
    if (!pdf) return;
    const bin = atob(pdf.pdf);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url; a.download = pdf.name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return <button type="button" disabled={state !== 'ready'} onClick={download} title={state === 'none' ? 'No PDF saved for this week' : ''}>Download PDF</button>;
}

const W = 800, H = 250, padL = 60, padR = 20, padT = 14, padB = 28;

function WeekChart({ weeks, sel, cur, onPick }: { weeks: Lineup[]; sel: number; cur: string; onPick: (i: number) => void }) {
  const fs = weeks.map(figures);
  const max = Math.max(1, ...fs.map((f) => f.target), ...fs.map((f) => f.cash ?? 0));
  const step = [50e3, 100e3, 200e3, 250e3, 500e3, 1e6].find((s) => max / s <= 4) ?? 1e6;
  const top = Math.ceil(max / step) * step;
  const ticks = Array.from({ length: top / step + 1 }, (_, i) => i * step);
  const n = weeks.length;
  const slot = (W - padL - padR) / n;
  const x = (i: number) => padL + slot * i + slot / 2;
  const y = (v: number) => padT + (H - padT - padB) * (1 - v / top);
  const st = weeks.map((w) => weekStatus(w.monday, cur));
  const cashIdx = weeks.map((_, i) => i).filter((i) => st[i] !== 'Next week' && fs[i].cash !== null);
  const doneIdx = cashIdx.filter((i) => st[i] === 'Complete');
  const line = (idx: number[], key: 'target' | 'cash') => idx.map((i) => `${x(i)},${y(fs[i][key] ?? 0)}`).join(' ');
  const lastDone = doneIdx[doneIdx.length - 1];
  const prog = cashIdx.find((i) => st[i] === 'In progress');
  const [hover, setHover] = useState<number | null>(null);
  const tipI = hover ?? sel;
  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="chart" role="img" aria-label={`Line chart of lineup target and collected for the weeks of ${short(weeks[0].monday)} to ${short(weeks[n - 1].monday)}`}>
        <rect x={x(sel) - slot / 2 + 4} y={8} width={slot - 8} height={H - padB - 4} rx={8} fill="var(--surf-3)" />
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} className="grid" />
            <text x={padL - 8} y={y(t) + 4} className="axis" textAnchor="end">{k(t)}</text>
          </g>
        ))}
        {weeks.map((w, i) => <text key={w.monday} x={x(i)} y={H - 8} className="axis" textAnchor="middle" style={{ fill: i === sel ? 'var(--text)' : undefined }}>{short(w.monday)}</text>)}
        <polyline fill="none" stroke={TARGET} strokeWidth={3} points={line(weeks.map((_, i) => i), 'target')} />
        {doneIdx.length > 1 && <polyline fill="none" stroke={CASH} strokeWidth={3} points={line(doneIdx, 'cash')} />}
        {prog !== undefined && lastDone !== undefined && <line x1={x(lastDone)} y1={y(fs[lastDone].cash ?? 0)} x2={x(prog)} y2={y(fs[prog].cash ?? 0)} stroke={CASH} strokeWidth={3} strokeDasharray="6 5" />}
        {weeks.map((w, i) => <circle key={'t' + w.monday} cx={x(i)} cy={y(fs[i].target)} r={4.5} fill={TARGET} />)}
        {cashIdx.map((i) => <circle key={'c' + weeks[i].monday} cx={x(i)} cy={y(fs[i].cash ?? 0)} r={4.5} fill={st[i] === 'In progress' ? 'var(--bg)' : CASH} stroke={CASH} strokeWidth={2} />)}
        {n <= 8 && weeks.map((w, i) => <text key={'tv' + w.monday} x={x(i)} y={y(fs[i].target) - 10} className="value" textAnchor="middle">{k(fs[i].target)}</text>)}
        {n <= 8 && cashIdx.map((i) => <text key={'cv' + weeks[i].monday} x={x(i)} y={y(fs[i].cash ?? 0) + 18} className="value" textAnchor="middle" style={{ fill: '#f5c27a' }}>{k(fs[i].cash ?? 0)}{st[i] === 'In progress' ? ' so far' : ''}</text>)}
      </svg>
      <div className="coll-hits">
        {weeks.map((w, i) => (
          <button key={w.monday} type="button" aria-label={`Show week of ${short(w.monday)}`} style={{ left: `${((padL + slot * i) / W) * 100}%`, width: `${(slot / W) * 100}%` }}
            onClick={() => onPick(i)} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(i)} onBlur={() => setHover(null)} />
        ))}
      </div>
      {hover != null && (
        <div className="tip" style={{ left: `${(x(tipI) / W) * 100}%` }}>
          <b>Week of {short(weeks[tipI].monday)}</b><br />Target {usd(fs[tipI].target)}
          {st[tipI] !== 'Next week' && <><br />Collected {fs[tipI].cash === null ? 'not refreshed yet' : usd(fs[tipI].cash)}</>}
        </div>
      )}
    </div>
  );
}

function PmBars({ w }: { w: Lineup }) {
  const pms = pmList(w);
  const max = Math.max(1, ...pms.map((p) => Math.max(p.target, p.collected)));
  return (
    <section className="card">
      <div className="coll-chart-head">
        <h2>By PM - week of {short(w.monday)}</h2>
        <div className="coll-legend">
          <span><i style={{ background: 'var(--sky-dim)' }} />Target</span>
          <span><i style={{ background: CASH }} />Collected on these jobs</span>
        </div>
      </div>
      {pms.length === 0 && <Empty>No jobs this week.</Empty>}
      {pms.map((p) => (
        <div key={p.pm} className="coll-pm">
          <div className="coll-pm-line">
            <span style={{ color: p.pm === 'No PM assigned' ? 'var(--st-risk)' : undefined }}>{p.pm}</span>
            <span className="coll-mono">{usd(p.target)} <span className="muted">- {p.jobs} {p.jobs === 1 ? 'job' : 'jobs'}</span></span>
          </div>
          <div className="coll-bar"><div style={{ width: `${(p.target / max) * 100}%`, background: 'var(--sky-dim)' }} /></div>
          <div className="coll-bar thin"><div style={{ width: `${(p.collected / max) * 100}%`, background: CASH }} /></div>
        </div>
      ))}
    </section>
  );
}

function Watch({ w, status }: { w: Lineup; status: Status }) {
  const deposits = w.rows.filter((r) => r.basis === '50% deposit');
  const behind = w.rows.filter((r) => r.behind > 0);
  const issues = w.issues ?? [];
  return (
    <section className="card">
      <h2>Watch list - {status === 'Next week' ? 'next week' : `week of ${short(w.monday)}`}</h2>
      {deposits.length > 0 && (
        <div className="coll-box amber">
          <div className="coll-box-title">Deposit due</div>
          {deposits.map((r) => (
            <div key={r.dealId}>
              <div className="coll-line"><span>{r.deal}</span><span className="coll-mono">{usd(r.target)}</span></div>
              <div className="coll-sub">{r.pm || 'No PM'} - {md(r.start)} - {md(r.end)} - 50% of {usd(r.contract)}</div>
            </div>
          ))}
        </div>
      )}
      {behind.length > 0 && (
        <div className="coll-box red">
          <div className="coll-box-title">Earlier weekly shares outstanding (not in totals)</div>
          {behind.map((r) => <div key={r.dealId} className="coll-line"><span>{r.deal}</span><span className="coll-mono">{usd(r.behind)}</span></div>)}
        </div>
      )}
      <div className="coll-box">
        <div className="coll-box-title">Data to clean up</div>
        {issues.length === 0 ? <div className="muted">Nothing to clean up.</div> : issues.map((t, i) => <div key={i} className="coll-issue">{t}</div>)}
      </div>
    </section>
  );
}

function JobsTable({ rows, showCollected }: { rows: LineupRow[]; showCollected: boolean }) {
  const [all, setAll] = useState(false);
  useEffect(() => setAll(false), [rows.length]);
  const shown = useMemo(() => (all ? rows : rows.slice(0, 12)), [rows, all]);
  if (!rows.length) return <Empty>No jobs in this view.</Empty>;
  return (
    <>
      <div className="coll-scroll">
        <table className="list coll-table">
          <thead><tr>
            <th>Scheduled</th><th>Deal</th><th>PM</th><th className="r">Contract</th><th className="r">Paid</th><th>Basis</th><th className="r">Target</th>{showCollected && <th className="r">Collected</th>}
          </tr></thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.dealId} className="no-click">
                <td className="coll-when">{r.start === r.end ? md(r.start) : `${md(r.start)} - ${md(r.end)}`}</td>
                <td>{r.deal}</td>
                <td style={{ color: r.pm ? undefined : 'var(--st-risk)' }}>{r.pm || 'Unassigned'}</td>
                <td className="r coll-mono">{usd(r.contract)}</td>
                <td className="r coll-mono muted">{r.paid ? usd(r.paid) : '-'}</td>
                <td className="muted">{r.basis}{r.type === 'repair' ? ' (repair)' : ''}</td>
                <td className="r coll-mono">{usd(r.target)}</td>
                {showCollected && <td className="r coll-mono">{r.collectedInWeek ? usd(r.collectedInWeek) : '-'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 12 && <button type="button" className="linkish" onClick={() => setAll((v) => !v)}>{all ? 'Show fewer' : `Show all ${rows.length} jobs`}</button>}
    </>
  );
}
